import {browserLocalStorage} from "core/browserStorage";
import {ATTRIBUTION_WINDOW_DAYS} from "./referralAttribution";
import type {ReferralOfferVariant} from "./referralOffer";

/**
 * The tier an anonymous visitor picked, carried across the sign-in that follows
 * (kitecorp/transferify-ui#2020).
 *
 * <b>A choice, never an identity.</b> What is written is one tier name and the referral code it was
 * picked under, and this browser already holds that code (`referralAttribution`'s first-touch entry).
 * Nothing here says anything new about the reader, which is the ticket's "no new per-reader
 * identifier" in the one place it would have been tempting to mint one.
 *
 * <b>Two bounds, and both are needed.</b> The stored entry expires after the same number of days the
 * attribution window runs for, which caps how long a forgotten choice can sit in a browser; and
 * {@link claimableReferralIntent} refuses one whose code is not the code this visit is acting on, which
 * is what actually stops a stale choice from resuming. The two windows share a LENGTH, not a start: the
 * code's begins at first touch and the choice's at the press, so a choice can outlive its code by up to
 * that length and only the code match catches it.
 *
 * <b>Every unreadable state answers NOTHING</b>, the opposite side from `referralOfferDismissal`'s
 * rule and for the same reasoning: a wrong "dismissed" hides a gift, and a wrong claim intent would
 * open a payment sheet nobody asked for. The failure falls on the side that charges no money.
 *
 * React-free by the repo's logic-outside-react mandate.
 */

/** Versioned, so a future change to what is stored invalidates the old value instead of misreading it. */
export const REFERRAL_CLAIM_INTENT_STORAGE_KEY = "referral.offer.claimIntent.v1";

/** The tier this browser remembers, and the link it was chosen under. */
export interface ReferralClaimIntent {
	variant: ReferralOfferVariant;
	/**
	 * The code this choice was made for: the one this browser will SUBMIT, which is the first-touch
	 * entry rather than whatever the arriving link carried. First touch is the attribution model, so
	 * a visitor who follows a second link while a live code is stored is still claiming the first
	 * friend's gift, and this is the code that gift is under.
	 */
	code: string;
}

interface StoredIntent extends ReferralClaimIntent {
	capturedAt: number;
}

/** The tiers this build can claim. A stored value outside them is discarded rather than cast. */
const VARIANTS: readonly string[] = ["plus", "premium"];

/**
 * Records the tier the reader picked, replacing any earlier one.
 *
 * REPLACES rather than keeps the first, unlike the first-touch code beside it: the code answers "who
 * introduced them", where the last answer would rewrite history, and this answers "what did they just
 * press", where the first answer would be stale the moment they changed their mind.
 *
 * @param now epoch milliseconds, injected so the window is testable without faking timers
 */
export function rememberReferralClaimIntent(intent: ReferralClaimIntent, now: number): void {
	const storage = browserLocalStorage();
	if (storage === null) {
		return;
	}
	try {
		storage.setItem(REFERRAL_CLAIM_INTENT_STORAGE_KEY,
			JSON.stringify({variant: intent.variant, code: intent.code, capturedAt: now} satisfies StoredIntent));
	} catch {
		// A full or blocked store costs the reader one press after signing up: the gift is still on the
		// account card and the create panel's strip. Nothing else, and never a thrown page.
	}
}

/**
 * The remembered choice, or `null` when there is none, it expired, or it is unreadable.
 *
 * An expired or unreadable entry is DELETED as it is read, so a later clock change cannot resurrect a
 * claim the window already closed. It is the discipline `storedReferralCode` applies to the code itself.
 */
export function storedReferralClaimIntent(now: number): ReferralClaimIntent | null {
	const stored = read();
	if (stored === null) {
		return null;
	}
	if (now - stored.capturedAt >= ATTRIBUTION_WINDOW_DAYS * 24 * 60 * 60 * 1000) {
		forgetReferralClaimIntent();
		return null;
	}
	return {variant: stored.variant, code: stored.code};
}

/** Forgets the choice: the claim completed, the gift was refused, or the reader stayed on Free. */
export function forgetReferralClaimIntent(): void {
	const storage = browserLocalStorage();
	if (storage === null) {
		return;
	}
	try {
		storage.removeItem(REFERRAL_CLAIM_INTENT_STORAGE_KEY);
	} catch {
		// See rememberReferralClaimIntent.
	}
}

function read(): StoredIntent | null {
	const storage = browserLocalStorage();
	if (storage === null) {
		return null;
	}
	let raw: string | null;
	try {
		raw = storage.getItem(REFERRAL_CLAIM_INTENT_STORAGE_KEY);
	} catch {
		return null;
	}
	if (raw === null) {
		return null;
	}
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		forgetReferralClaimIntent();
		return null;
	}
	if (!isStoredIntent(parsed)) {
		forgetReferralClaimIntent();
		return null;
	}
	return parsed;
}

function isStoredIntent(value: unknown): value is StoredIntent {
	if (typeof value !== "object" || value === null) {
		return false;
	}
	const candidate = value as Partial<StoredIntent>;
	return typeof candidate.variant === "string" && VARIANTS.includes(candidate.variant)
		&& typeof candidate.code === "string" && candidate.code !== ""
		&& typeof candidate.capturedAt === "number";
}

/**
 * The remembered tier this visit may act on, or `null` when the choice is not about the code in hand.
 *
 * <b>This is what makes the stored code load-bearing rather than decorative.</b> Two things it refuses,
 * and neither is theoretical:
 *
 *  - A choice made under one link, read while the browser is acting on ANOTHER. The visitor is claiming
 *    the first-touch friend's gift, so a tier chosen for a different link's offer does not preselect
 *    this one and does not resume against it.
 *  - A choice with no live code beside it at all: the window closed and `storedReferralCode` deleted the
 *    entry as it read it. Without this the stale choice would keep the landing asking `/plan` on every
 *    page load for the rest of the entry's own month, for an offer nothing can claim.
 *
 * @param code the code this visit is acting on: the one the attribution submitted once it has, and the
 *             arriving link's before that
 */
export function claimableReferralIntent(
	intent: ReferralClaimIntent | null, code: string | null,
): ReferralOfferVariant | null {
	return intent !== null && code !== null && intent.code === code ? intent.variant : null;
}
